import { useSyncExternalStore } from 'react';

/**
 * useMenuMode — TRYB rysowania lewego menu tenanta (redesign menu 2026-09).
 *
 * Prostopadłe do „Widoku menu" (który ZESTAW pozycji: Systemowe/Kadry/custom — to żyje
 * w `TenantMenuViewSwitcher`). Tu chodzi o SPOSÓB prezentacji całego menu:
 *   - `list`    — obecne, płaska lista pozycji wybranego widoku (domyślny, nietknięty).
 *   - `tree`    — drzewko obszar → moduł → kartoteka (pokazuje wszystko naraz).
 *   - `columns` — pasek ikon obszarów + kolumny modułów/kartotek (jeszcze nieprojektowany).
 *
 * Trzymany w localStorage (per przeglądarka) — świadomie lekki start, jak `sidebar.skin`.
 * Przeniesienie na preferencję serwerową per-user (jak wybór widoku przez /auth/menu-preference)
 * zostawiamy na później. Zewnętrzny store (useSyncExternalStore) zamiast useState, żeby
 * przełącznik w nagłówku i sam sidebar (osobne miejsca w drzewie) reagowały na tę samą zmianę.
 */
export type MenuMode = 'list' | 'tree' | 'columns';

const STORAGE_KEY = 'tenant.menu.mode';
const MODES: ReadonlySet<string> = new Set(['list', 'tree', 'columns']);

let current: MenuMode = readInitial();
const listeners = new Set<() => void>();

function readInitial(): MenuMode {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && MODES.has(raw)) return raw as MenuMode;
  } catch {
    // localStorage niedostępny (tryb prywatny / zablokowane) — domyślny tryb.
  }
  return 'list';
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function getSnapshot(): MenuMode {
  return current;
}

/** Ustaw tryb menu — zapisuje do localStorage i budzi wszystkich subskrybentów. */
export function setMenuMode(mode: MenuMode): void {
  if (mode === current) return;
  current = mode;
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // brak trwałości = tryb działa do końca sesji; nie blokujemy zmiany.
  }
  for (const cb of listeners) cb();
}

/** Bieżący tryb menu + setter. Re-render przy zmianie z dowolnego miejsca. */
export function useMenuMode(): { mode: MenuMode; setMode: (mode: MenuMode) => void } {
  const mode = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { mode, setMode: setMenuMode };
}
